import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Check, MicOff, Trash2, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import {
  listenToModerationQueue,
  removeComment,
  restoreComment,
} from '../../database/profileComments';
import {
  listenToNewsModerationQueue,
  removeComment as removeNewsComment,
  restoreComment as restoreNewsComment,
} from '../../database/newsComments';
import {
  clearModerationStatus,
  isMuteActive,
  listenToActiveModerationStatuses,
  setModerationStatus,
} from '../../database/userModerationStatus';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const formatTs = (ts) => {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleString();
};

const muteOptions = [
  { label: '15 minutes', ms: 15 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
];

const BanDialog = ({ open, onOpenChange, target, modUid, defaultMode = 'commentBan' }) => {
  const [mode, setMode] = useState(defaultMode);
  const [reason, setReason] = useState('');
  const [muteMs, setMuteMs] = useState(muteOptions[1].ms);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setReason('');
      setMuteMs(muteOptions[1].ms);
    }
  }, [open, defaultMode]);

  const submit = async () => {
    if (!target?.uid) return;
    setBusy(true);
    try {
      const payload = {
        commentBan: mode === 'commentBan',
        siteBan: mode === 'siteBan',
        muteUntil: mode === 'mute' ? new Date(Date.now() + muteMs) : null,
        reason: reason.trim(),
      };
      await setModerationStatus(target.uid, payload, modUid);
      toast.success('Moderation action applied.');
      onOpenChange(false);
    } catch (err) {
      toast.error('Action failed.', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply moderation action to {target?.username || 'user'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant={mode === 'commentBan' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('commentBan')}
            >
              <UserMinus className="h-3.5 w-3.5" />
              Comment ban
            </Button>
            <Button
              variant={mode === 'mute' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('mute')}
            >
              <MicOff className="h-3.5 w-3.5" />
              Mute
            </Button>
            <Button
              variant={mode === 'siteBan' ? 'destructive' : 'outline'}
              size="sm"
              onClick={() => setMode('siteBan')}
            >
              <Ban className="h-3.5 w-3.5" />
              Site ban
            </Button>
          </div>

          {mode === 'mute' && (
            <div className="flex flex-wrap gap-2">
              {muteOptions.map((o) => (
                <Button
                  key={o.ms}
                  variant={muteMs === o.ms ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setMuteMs(o.ms)}
                >
                  {o.label}
                </Button>
              ))}
            </div>
          )}

          <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Reason (optional)
            </div>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Visible to the user on the suspended page"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReportedCommentRow = ({ comment: c, context, onRestore, onRemove, onBan }) => (
  <li className="grid grid-cols-[auto_1fr_auto] gap-3 py-4">
    <Avatar className="h-9 w-9">
      {c.authorPhotoURL && <AvatarImage src={c.authorPhotoURL} alt="" />}
      <AvatarFallback className="text-xs">{initials(c.authorUsername)}</AvatarFallback>
    </Avatar>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to={`/user/${c.authorUserId}`} className="font-display font-medium hover:underline">
          {c.authorUsername || 'Player'}
        </Link>
        {context}
        <Badge variant="outline" className="text-warning">
          {c.reportCount} report{c.reportCount === 1 ? '' : 's'}
        </Badge>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {formatTs(c.createdAt)}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm">{c.text}</p>
    </div>
    <div className="flex shrink-0 flex-col gap-1.5">
      <Button size="sm" variant="outline" onClick={() => onRestore(c.id)}>
        <Check className="h-3.5 w-3.5" />
        Restore
      </Button>
      <Button size="sm" variant="outline" onClick={() => onRemove(c.id)}>
        <Trash2 className="h-3.5 w-3.5" />
        Remove
      </Button>
      {onBan && (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onBan({ uid: c.authorUserId, username: c.authorUsername })}
        >
          <UserMinus className="h-3.5 w-3.5" />
          Ban author
        </Button>
      )}
    </div>
  </li>
);

const ModerationQueue = () => {
  const { currentUser, hasPermission } = useContext(AuthContext);
  const canModeratePinWall = hasPermission('profileComments.moderate');
  const canModerateNews = hasPermission('news.manage') || canModeratePinWall;

  const [queue, setQueue] = useState([]);
  const [newsQueue, setNewsQueue] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingNewsQueue, setLoadingNewsQueue] = useState(true);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [banTarget, setBanTarget] = useState(null);

  useEffect(() => {
    if (!canModeratePinWall) return undefined;
    setLoadingQueue(true);
    return listenToModerationQueue((rows) => {
      setQueue(rows);
      setLoadingQueue(false);
    });
  }, [canModeratePinWall]);

  useEffect(() => {
    if (!canModerateNews) return undefined;
    setLoadingNewsQueue(true);
    return listenToNewsModerationQueue((rows) => {
      setNewsQueue(rows);
      setLoadingNewsQueue(false);
    });
  }, [canModerateNews]);

  useEffect(() => {
    if (!canModeratePinWall) return undefined;
    setLoadingStatuses(true);
    return listenToActiveModerationStatuses((rows) => {
      setStatuses(rows.filter(Boolean));
      setLoadingStatuses(false);
    });
  }, [canModeratePinWall]);

  const activeStatuses = useMemo(
    () => statuses.filter((s) => s.commentBan || s.siteBan || isMuteActive(s)),
    [statuses]
  );

  if (!canModerateNews) {
    return (
      <div className="px-6 py-12 text-sm text-muted-foreground">
        You don't have permission to moderate comments.
      </div>
    );
  }

  const onRestore = async (commentId) => {
    try {
      await restoreComment(commentId);
      toast.success('Comment restored.');
    } catch (err) {
      toast.error('Restore failed.', { description: err.message });
    }
  };

  const onRemove = async (commentId) => {
    if (!window.confirm('Remove this comment? It will stay archived but hidden from users.')) {
      return;
    }
    try {
      await removeComment(commentId, currentUser.uid);
      toast.success('Comment removed.');
    } catch (err) {
      toast.error('Remove failed.', { description: err.message });
    }
  };

  const onRestoreNews = async (commentId) => {
    try {
      await restoreNewsComment(commentId);
      toast.success('Comment restored.');
    } catch (err) {
      toast.error('Restore failed.', { description: err.message });
    }
  };

  const onRemoveNews = async (commentId) => {
    if (!window.confirm('Remove this comment? It will stay archived but hidden from users.')) {
      return;
    }
    try {
      await removeNewsComment(commentId, currentUser.uid);
      toast.success('Comment removed.');
    } catch (err) {
      toast.error('Remove failed.', { description: err.message });
    }
  };

  const onClearStatus = async (uid) => {
    if (!window.confirm('Clear all moderation actions for this user?')) return;
    try {
      await clearModerationStatus(uid);
      toast.success('Moderation cleared.');
    } catch (err) {
      toast.error('Clear failed.', { description: err.message });
    }
  };

  return (
    <div className="mx-auto w-full max-w-screen-lg space-y-8 px-4 pb-24 pt-8 sm:px-6">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Moderation
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold">Reported comments</h1>
      </div>

      {canModerateNews && (
        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <CardTitle className="font-display text-lg">
              News comments {newsQueue.length > 0 && `· ${newsQueue.length}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingNewsQueue ? (
              <div className="h-20 animate-pulse rounded-md bg-muted/40" />
            ) : newsQueue.length === 0 ? (
              <p className="text-sm text-muted-foreground">No reports right now.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {newsQueue.map((c) => (
                  <ReportedCommentRow
                    key={c.id}
                    comment={c}
                    context={
                      <Link
                        to={`/admin/news/${c.newsId}`}
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
                      >
                        on news
                      </Link>
                    }
                    onRestore={onRestoreNews}
                    onRemove={onRemoveNews}
                    onBan={canModeratePinWall ? setBanTarget : undefined}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {canModeratePinWall && (
        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <CardTitle className="font-display text-lg">
              Pin-wall comments {queue.length > 0 && `· ${queue.length}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingQueue ? (
              <div className="h-20 animate-pulse rounded-md bg-muted/40" />
            ) : queue.length === 0 ? (
              <p className="text-sm text-muted-foreground">No reports right now.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {queue.map((c) => (
                  <ReportedCommentRow
                    key={c.id}
                    comment={c}
                    context={
                      <>
                        <span className="text-muted-foreground">on</span>
                        <Link
                          to={`/user/${c.ownerUserId}`}
                          className="font-display font-medium hover:underline"
                        >
                          wall {c.ownerUserId.slice(0, 6)}…
                        </Link>
                      </>
                    }
                    onRestore={onRestore}
                    onRemove={onRemove}
                    onBan={setBanTarget}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {canModeratePinWall && (
        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <CardTitle className="font-display text-lg">
              Active moderation statuses {activeStatuses.length > 0 && `· ${activeStatuses.length}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStatuses ? (
              <div className="h-12 animate-pulse rounded-md bg-muted/40" />
            ) : activeStatuses.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nobody is currently banned or muted.</p>
            ) : (
              <ul className="divide-y divide-border/60">
                {activeStatuses.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <Link
                        to={`/user/${s.id}`}
                        className="font-display text-sm font-medium hover:underline"
                      >
                        {s.id}
                      </Link>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {s.siteBan && (
                          <Badge variant="outline" className="text-destructive">
                            Site ban
                          </Badge>
                        )}
                        {s.commentBan && <Badge variant="outline">Comment ban</Badge>}
                        {isMuteActive(s) && (
                          <Badge variant="outline">Muted until {formatTs(s.muteUntil)}</Badge>
                        )}
                      </div>
                      {s.reason && <p className="mt-1 text-xs text-muted-foreground">{s.reason}</p>}
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBanTarget({ uid: s.id, username: s.id })}
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onClearStatus(s.id)}>
                        Clear
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <BanDialog
        open={!!banTarget}
        onOpenChange={(o) => !o && setBanTarget(null)}
        target={banTarget}
        modUid={currentUser?.uid}
      />
    </div>
  );
};

export default ModerationQueue;
